import { useQuery } from "@tanstack/react-query";
import { UserPlus } from "lucide-react";
import { api, errorMessage, unwrap } from "../api/client";
import { useOrg, useOrgId } from "../api/org";
import { Alert, Badge, Card, PageHead, Spinner } from "../components/ui";
import { initials } from "../lib/format";

const ROLE_TEXT = {
  owner: "Everything, including billing",
  admin: "Settings, keys, branding and credits",
  member: "Sees designs and leads",
} as const;

export function Team() {
  const orgId = useOrgId();
  const { me } = useOrg();
  const members = useQuery({
    queryKey: ["members", orgId],
    queryFn: () => unwrap(api.GET("/v1/orgs/{org_id}/members", { params: { path: { org_id: orgId } } })),
  });
  return (
    <>
      <PageHead title="Team" description="People who can sign in to this company's dashboard." />
      <div className="stack">
        <Card pad={false}>
          {members.isLoading ? (
            <Spinner />
          ) : members.error ? (
            <div className="card-body">
              <Alert tone="danger">{errorMessage(members.error)}</Alert>
            </div>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Member</th>
                    <th>Role</th>
                    <th className="hide-sm">Can</th>
                  </tr>
                </thead>
                <tbody>
                  {members.data!.items.map((m) => (
                    <tr key={m.user_id}>
                      <td>
                        <div className="row" style={{ flexWrap: "nowrap" }}>
                          <div className="org-avatar" style={{ width: 32, height: 32, borderRadius: 99 }}>
                            {initials(m.display_name || m.email)}
                          </div>
                          <div style={{ minWidth: 0 }}>
                            <div className="cell-main ellipsis">
                              {m.display_name || m.email}
                              {m.user_id === me.user.id && <span className="faint"> (you)</span>}
                            </div>
                            {m.display_name && <div className="cell-sub">{m.email}</div>}
                          </div>
                        </div>
                      </td>
                      <td>
                        <Badge tone={m.role === "owner" ? "grad" : m.role === "admin" ? "info" : undefined} plain>
                          {m.role}
                        </Badge>
                      </td>
                      <td className="hide-sm muted">{ROLE_TEXT[m.role]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
        {/* TODO(T6): invitations need a backend feature (invite by email, accept on first sign-in, change roles). */}
        <Alert tone="info" title="Inviting teammates is coming soon.">
          <span className="row" style={{ display: "inline-flex", gap: 6 }}>
            <UserPlus size={14} /> Until then, contact SolarOS support to add a colleague to your company.
          </span>
        </Alert>
      </div>
    </>
  );
}
